// The settings the command palette can find (spec/13 §2, §13; spec/14 §14): each reachable settings
// screen's view, read the way the screen reads it and cached under the screen's own query key, so
// opening the palette warms the settings screens and opening a screen warms the palette.
//
// Read only while the palette is open. A screen the member may not read (`403`) is simply not
// searched — the server's answer, not a second list of rules here.

import type { SettingsView } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { useQueries } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { useApi } from '../api/client';
import { FIELDS } from '../venue-profile/fields';
import { optionWords, useRegistryLabels, useSettingHelp } from './labels';
import { readSettings, settingsKey } from './registry-source';
import { buildSettingsIndex, rankSettings, type IndexWords, type SettingDoc, type SettingsTarget } from './search';

const VENUE_FIELDS: readonly string[] = FIELDS.map((field) => field.key);

/** Each screen's view, or undefined; module-level so the combined result keeps its identity between renders. */
function viewsOf(results: readonly { readonly data?: { readonly data: SettingsView } | undefined }[]): (SettingsView | undefined)[] {
  return results.map((result) => result.data?.data);
}

export interface SettingsSearch {
  /** The ranked settings for a query; none until a screen has loaded, or for a query under two letters. */
  readonly search: (query: string) => readonly SettingDoc[];
}

export function useSettingsSearch(targets: readonly SettingsTarget[], enabled: boolean): SettingsSearch {
  const api = useApi();
  const { t } = useTranslation('console');
  const labels = useRegistryLabels();
  const helpOf = useSettingHelp();

  const loadedViews = useQueries({
    queries: targets.map((target) => ({
      queryKey: settingsKey(target.scope),
      queryFn: () => readSettings(api, target.scope),
      enabled,
      refetchOnWindowFocus: false,
    })),
    combine: viewsOf,
  });

  const words = useMemo<IndexWords>(
    () => ({
      label: (key) => labels.field(key, []),
      help: (key) => helpOf(key),
      options: (entry) => optionWords(entry, labels),
      groupLabel: (group: string, view: SettingsView | undefined) =>
        t(`settings.group.${group}`, { defaultValue: view?.groups.find((heading) => heading.id === group)?.title ?? group }),
      venueField: (key) => ({
        label: t(`venueProfile.label.${key}`, { defaultValue: key }),
        description: t(`venueProfile.description.${key}`, { defaultValue: '' }),
      }),
    }),
    [labels, t, helpOf],
  );

  const index = useMemo(
    () => (enabled ? buildSettingsIndex(targets.map((target, i) => ({ target, view: loadedViews[i] })), VENUE_FIELDS, words) : []),
    [enabled, targets, loadedViews, words],
  );

  const search = useCallback((query: string) => rankSettings(index, query), [index]);
  return { search };
}
